import { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import './commercial-hero.css';

/**
 * GalleryModal - Full-screen image gallery modal with navigation controls.
 * Supports keyboard navigation (left/right arrows, Escape to close).
 */
const GalleryModal = ({ show, onHide, images, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Reset to initialIndex when modal opens or initialIndex changes
  useEffect(() => {
    if (show) {
      setCurrentIndex(initialIndex);
    }
  }, [show, initialIndex]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images]);

  // Keyboard navigation
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrev();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [show, goToPrev, goToNext]);

  if (!images || images.length === 0) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen
      className="commercial-hero__gallery-modal"
      aria-label="Property image gallery"
    >
      <Modal.Body className="commercial-hero__gallery-modal-body">
        {/* Close button */}
        <button
          className="commercial-hero__gallery-modal-close"
          onClick={onHide}
          aria-label="Close gallery"
        >
          <FiX />
        </button>

        {/* Image counter */}
        <div className="commercial-hero__gallery-modal-counter">
          {currentIndex + 1} / {images.length}
        </div>

        {/* Previous button */}
        <button
          className="commercial-hero__gallery-modal-nav commercial-hero__gallery-modal-nav--prev"
          onClick={goToPrev}
          aria-label="Previous image"
        >
          <FiChevronLeft />
        </button>

        {/* Current image */}
        <div className="commercial-hero__gallery-modal-image-container">
          <img
            src={images[currentIndex]?.url}
            alt={images[currentIndex]?.alt || `Property image ${currentIndex + 1}`}
            className="commercial-hero__gallery-modal-image"
          />
        </div>

        {/* Next button */}
        <button
          className="commercial-hero__gallery-modal-nav commercial-hero__gallery-modal-nav--next"
          onClick={goToNext}
          aria-label="Next image"
        >
          <FiChevronRight />
        </button>
      </Modal.Body>
    </Modal>
  );
};

export default GalleryModal;
